import {
  ElementRef,
  FC,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useState,
} from "react";
import Select from "react-select";
import Creatable from "react-select/creatable";
import styled from "styled-components";
import _uniqueId from "lodash/uniqueId";

import { getOptionsMaxHeight } from "./dropdown.utils";

export enum DropdownTestIds {
  Component = "dropdown",
}

import {
  Control,
  DropdownContextProvider,
  DropdownIndicator,
  Group,
  GroupHeading,
  Input,
  Menu,
  MenuList,
  MultiValue,
  Option as OptionComponent,
  Placeholder,
  SingleValue,
  ValueContainer,
} from "./components";
import { alpha } from "../../typography";
import {
  Option,
  OptionGroup,
  DropdownVariant,
  DropdownProps,
  MultiSelectDropdownProps,
} from "./types";
import { NoOptionsMessage } from "./components/NoOptionsMessage";
import { ClearIndicator } from "./components/ClearIndicator";
import { useDropdown } from "./useDropdown";

type LabelProps = {
  $isDisabled: boolean;
  $hasError: boolean;
};

const Label = styled.div<LabelProps>`
  margin-bottom: 4px;
  ${({ $isDisabled }) =>
    $isDisabled ? alpha.primaryInactive.body3 : alpha.primaryActive.body3};
  ${({ $hasError }) => $hasError && alpha.error.body3};
`;

const Asterisk = styled.span`
  ${alpha.error.body3};
`;

const ErrorMessage = styled.div`
  ${alpha.error.caption};
`;

const overrideComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorSeparator: () => null,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option: OptionComponent,
  Placeholder,
  SingleValue,
};

const multiSelectOverrideComponents = {
  ...overrideComponents,
  MultiValue,
  ValueContainer,
};

const isDropdownOptionGroup = (
  value: Option | OptionGroup
): value is OptionGroup => {
  return "options" in value;
};

type DropdownContainerProps = {
  id: string;
  testId?: string;
  label?: string;
  variant: DropdownVariant;
  options: Option[] | OptionGroup[];
  isRequired: boolean;
  errorMessage: string;
  allowIcons: boolean;
  hasSelectedOption?: boolean;
  children: ReactNode;
  name: string;
};

const DropdownContainer: FC<DropdownContainerProps> = ({
  id,
  name,
  testId,
  label,
  variant,
  options,
  isRequired,
  errorMessage,
  allowIcons,
  hasSelectedOption,
  children,
}) => {
  const hasError = errorMessage.length > 0;
  const hasAnyIcons = options.some((value: Option | OptionGroup) => {
    if (isDropdownOptionGroup(value)) {
      return value.options.some((option) => option.iconName);
    }
    return value.iconName;
  });
  return (
    <div id={id} data-testid={testId ?? DropdownTestIds.Component}>
      <label htmlFor={name || id}>
        {label && (
          <Label
            $isDisabled={variant === "DISABLED" || variant === "READONLY"}
            $hasError={hasError}
          >
            {label} {isRequired ? <Asterisk>*</Asterisk> : null}
          </Label>
        )}
        <DropdownContextProvider
          hasAnyIcons={hasAnyIcons}
          allowIcons={allowIcons}
          hasError={hasError}
          variant={variant}
          hasSelectedOption={hasSelectedOption}
        >
          {children}
        </DropdownContextProvider>
      </label>
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

export const Dropdown: FC<DropdownProps> = forwardRef(
  (
    {
      testId,
      label,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Select>>
  ) => {
    const [id] = useState(_uniqueId("single-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    return (
      <DropdownContainer
        id={id}
        testId={testId}
        label={label}
        variant={variant}
        options={options}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
        name={name}
      >
        <Select
          maxMenuHeight={getOptionsMaxHeight(options, maxOptionsToShow)}
          {...dropdownProps}
          ref={ref}
          id={id}
          name={name}
          isMulti={false}
          components={overrideComponents}
          closeMenuOnSelect
          value={value}
          onChange={(value, action) => {
            if (value && action.action === "select-option") {
              const option = value as Option;
              if (option !== selected) {
                setSelected(option);
                onSelect({ selected: option });
              }
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues[0] as Option });
            }
          }}
        />
      </DropdownContainer>
    );
  }
);

export const MultiSelectDropdown: FC<MultiSelectDropdownProps> = forwardRef(
  (
    {
      testId,
      label,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onDeselect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Select>>
  ) => {
    const [id] = useState(_uniqueId("multi-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    return (
      <DropdownContainer
        id={id}
        name={name}
        testId={testId}
        label={label}
        variant={variant}
        options={options}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
      >
        <Select
          {...dropdownProps}
          ref={ref}
          id={id}
          isMulti
          components={multiSelectOverrideComponents}
          closeMenuOnSelect={false}
          maxMenuHeight={getOptionsMaxHeight(options, maxOptionsToShow)}
          value={value}
          onChange={(value, action) => {
            if (value && action.action === "select-option") {
              const option = action.option as Option;
              if (selected?.includes(option)) {
                setSelected((prevSelected) =>
                  prevSelected?.filter(
                    (selectedOption) => selectedOption !== option
                  )
                );
                onDeselect({ deselected: option });
              } else {
                setSelected((prevSelected) => [
                  ...(prevSelected || []),
                  option,
                ]);
                onSelect({ selected: option });
              }
            }
            if (action.action === "remove-value") {
              const option = action.removedValue as Option;
              setSelected((prevSelected) =>
                prevSelected?.filter(
                  (selectedOption) => selectedOption !== option
                )
              );
              onDeselect({ deselected: option });
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues as Option[] });
            }
          }}
        />
      </DropdownContainer>
    );
  }
);

export const CreatableDropdown: FC<DropdownProps> = forwardRef(
  (
    {
      testId,
      label,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Creatable>>
  ) => {
    const [id] = useState(_uniqueId("single-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    return (
      <DropdownContainer
        id={id}
        testId={testId}
        label={label}
        variant={variant}
        options={options}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
        name={name}
      >
        <Creatable
          maxMenuHeight={getOptionsMaxHeight(options, maxOptionsToShow)}
          {...dropdownProps}
          ref={ref}
          id={id}
          name={name}
          isMulti={false}
          components={overrideComponents}
          closeMenuOnSelect
          value={value}
          onChange={(value, action) => {
            if (
              value &&
              (action.action === "select-option" ||
                action.action === "create-option")
            ) {
              const option = value as Option;
              if (option !== selected) {
                setSelected(option);
                onSelect({ selected: option });
              }
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues[0] as Option });
            }
          }}
        />
      </DropdownContainer>
    );
  }
);
